import React from 'react';
import { Box, Container, Typography, List, ListItem, ListItemText, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import GppGoodIcon from '@mui/icons-material/GppGood';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import TimelineIcon from '@mui/icons-material/Timeline';
import GroupsIcon from '@mui/icons-material/Groups';

const Section = styled(Paper)(({ theme }) => ({
  padding: '2.5rem',
  marginBottom: '2rem',
  background: '#FFFFFF',
  borderRadius: '20px',
  border: '1px solid #E2E8F0',
  '&:last-child': {
    marginBottom: 0
  }
}));

const SectionTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  marginBottom: '2rem',
  '& .icon': {
    width: '40px',
    height: '40px',
    color: '#1A365D'
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  color: '#1A365D',
  fontSize: '1.75rem',
  fontWeight: 700,
  position: 'relative',
  paddingBottom: '0.75rem',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '60px',
    height: '3px',
    background: 'linear-gradient(135deg, #1A365D 0%, #004B87 100%)'
  }
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: '0.75rem 0',
  '& .MuiListItemText-root': {
    margin: 0
  },
  '&:before': {
    content: '"•"',
    color: '#1A365D',
    fontWeight: 'bold',
    fontSize: '1.2rem',
    marginRight: '1rem'
  }
}));

const SubSection = styled(Box)(({ theme }) => ({
  // background: 'linear-gradient(135deg, rgba(26, 54, 93, 0.03) 0%, rgba(0, 75, 135, 0.03) 100%)',
  borderRadius: '12px',
  padding: '2rem',
  marginTop: '2rem'
}));

const CharteL2MI = () => {
  return (
    <Container maxWidth="xl" sx={{ py: { xs: 4, md: 8 }, px: { xs: 2, sm: 4, md: 8, lg: 12 } }}>
      <Section elevation={0}>
        <SectionTitle>
          <GppGoodIcon className="icon" />
          <Title variant="h2">
            ENGAGEMENT ET POLITIQUE QUALITÉ
          </Title>
        </SectionTitle>
        <Typography sx={{ color: '#475569', fontSize: '1.1rem', lineHeight: 1.7, mb: 2 }}>
          Notre politique est orientée vers nos clients, en vue de les satisfaire, voir même de dépasser leurs attentes. Pour y
          parvenir, nous avons mis en place un système de management qualité conforme à la norme ISO/CEI 17025 V2017.
        </Typography>
        <Typography sx={{ color: '#475569', fontSize: '1.1rem', lineHeight: 1.7, mb: 2 }}>
          Notre organisation mise en place et les actions engagées s'attachent à assurer et à préserver l'impartialité et
          l'indépendance de tout le personnel du L2Mi.
        </Typography>
      </Section>

      <Section elevation={0}>
        <SectionTitle>
          <VerifiedUserIcon className="icon" />
          <Title variant="h2">
            NOS ENGAGEMENTS
          </Title>
        </SectionTitle>
        <Typography sx={{ color: '#475569', fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Dans cette optique nous nous sommes engagés à :
        </Typography>
        <List sx={{ pl: 2 }}>
          <StyledListItem>
            <ListItemText 
              primary="Réaliser nos activités en toute impartialité ne permettre à aucune personne quelconque de compromettre cette impartialité"
            />
          </StyledListItem>
          <StyledListItem>
            <ListItemText 
              primary="Respecter et faire respecter les exigences de la norme ISO/CEI 17025 V 2017"
            />
          </StyledListItem>
          <StyledListItem>
            <ListItemText 
              primary="Respecter les exigences du client, légales et réglementaires, et de l'organisme d'accréditation"
            />
          </StyledListItem>
          <StyledListItem>
            <ListItemText 
              primary="Assurer la pérennité de l'entreprise grâce à l'amélioration de la technicité des prestations et services"
            />
          </StyledListItem>
          <StyledListItem>
            <ListItemText 
              primary="Veiller à ce que l'ensemble du personnel prenne connaissance de la documentation qualité et applique les politiques et les procédures dans leurs activités"
            />
          </StyledListItem>
        </List>

        <List sx={{ pl: 4 }}>
          <StyledListItem sx={{ 
            '&:before': { 
              content: '"○"',
              color: '#64748B',
              fontSize: '0.8rem'
            },
            padding: '0.5rem 0'
          }}>
            <ListItemText 
              primary="S'engage à respecter le code déontologique du L2Mi et à exercer les activités en toute impartialité"
              sx={{ '& .MuiListItemText-primary': { fontSize: '0.95rem', color: '#64748B' } }}
            />
          </StyledListItem>
          <StyledListItem sx={{ 
            '&:before': { 
              content: '"○"',
              color: '#64748B',
              fontSize: '0.8rem'
            },
            padding: '0.5rem 0'
          }}>
            <ListItemText 
              primary="N'accepte aucun engagement dans d'autres activités, qui réduiraient la confiance de sa compétence, de son impartialité, de son jugement ou de son intégrité opérationnelle"
              sx={{ '& .MuiListItemText-primary': { fontSize: '0.95rem', color: '#64748B' } }}
            />
          </StyledListItem>
          <StyledListItem sx={{ 
            '&:before': { 
              content: '"○"',
              color: '#64748B',
              fontSize: '0.8rem'
            },
            padding: '0.5rem 0'
          }}>
            <ListItemText 
              primary="Assurer et préserver la confidentialité de toutes les informations obtenues ou générées au cours de ses activités du laboratoire"
              sx={{ '& .MuiListItemText-primary': { fontSize: '0.95rem', color: '#64748B' } }}
            />
          </StyledListItem>
          <StyledListItem sx={{ 
            '&:before': { 
              content: '"○"',
              color: '#64748B',
              fontSize: '0.8rem'
            },
            padding: '0.5rem 0'
          }}>
            <ListItemText 
              primary="Informer le client ou la personne concernée, des informations que le laboratoire est tenu à divulguer"
              sx={{ '& .MuiListItemText-primary': { fontSize: '0.95rem', color: '#64748B' } }}
            />
          </StyledListItem>
          <StyledListItem sx={{ 
            '&:before': { 
              content: '"○"',
              color: '#64748B',
              fontSize: '0.8rem'
            },
            padding: '0.5rem 0'
          }}>
            <ListItemText 
              primary="Améliorer en continu les performances du système de management qualité"
              sx={{ '& .MuiListItemText-primary': { fontSize: '0.95rem', color: '#64748B' } }}
            />
          </StyledListItem>
        </List>

      </Section>

      <Section elevation={0}>
        <SectionTitle>
          <TimelineIcon className="icon" />
          <Title variant="h2">
            NOS OBJECTIFS
          </Title>
        </SectionTitle>
        <Typography sx={{ color: '#475569', fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Parallèlement à la mise en œuvre de notre système de management, nous nous sommes fixés les objectifs suivants :
        </Typography>
        <List sx={{ pl: 2 }}>
          <StyledListItem>
            <ListItemText 
              primary="Fidéliser nos clients et s'assurer de leur degré de satisfaction"
            />
          </StyledListItem>
          <StyledListItem>
            <ListItemText 
              primary="Augmenter le chiffre d'affaire de l'année actuelle par rapport à l'année précédente d'au moins 2%"
            />
          </StyledListItem>
          <StyledListItem>
            <ListItemText 
              primary="Se démarquer par rapport à la concurrence en offrant à notre clientèle, outre les travaux d'étalonnage et vérification, un service de qualité additionnel en prodiguant conseils et assistance technique"
            />
          </StyledListItem>
          <StyledListItem>
            <ListItemText primary="Etendre l'accréditation du laboratoire" />
          </StyledListItem>
          <StyledListItem>
            <ListItemText 
              primary="Assurer et maintenir la qualification de nos opérateurs pour réaliser les activités qui leur sont attribuées et identifier les écarts"
            />
          </StyledListItem>
          <StyledListItem>
            <ListItemText primary="Assurer l'impartialité et la cohérence des activités" />
          </StyledListItem>
        </List>
      </Section>

      <Section elevation={0}>
        <SectionTitle>
          <GroupsIcon className="icon" />
          <Title variant="h2">
            NOTRE ENGAGEMENT QUALITÉ
          </Title>
        </SectionTitle>
        <SubSection>
          <Typography sx={{ color: '#475569', fontSize: '1.1rem', lineHeight: 1.7, mb: 4 }}>
            Le responsable qualité nous est directement rattachée pour promouvoir et suivre les évolutions du système de
            management de la qualité. Toutes les actions qu'il entreprend dans l'accomplissement de sa tâche sont assurées de
            notre soutien et assistance.
          </Typography>

          <Typography sx={{ 
            color: '#1A365D', 
            fontSize: '1.1rem', 
            lineHeight: 1.7, 
            fontStyle: 'italic',
            textAlign: 'center',
            borderLeft: '3px solid #1A365D',
            paddingLeft: '1.5rem',
            margin: '2rem 0'
          }}>
            Nous comptons sur la mobilisation et l'enthousiasme de tout le personnel, car nos objectifs forts ambitieux ne
            peuvent être atteints que grâce à la contribution de chacun d'entre nous.
          </Typography>
        </SubSection>
      </Section>
    </Container>
  );
};

export default CharteL2MI;
